import { Component, OnInit, Renderer2, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators, AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { ConstantsService } from '../services/constants.service';
import { Auth } from 'aws-amplify';
import { Router } from '@angular/router';
import { CacheService } from '../services/cache.service';
import { UserService } from '../services/user.service';
import { DataService } from '../services/data.service';
import { UrlConstants } from '../services/url-constants';
import { Subscription } from 'rxjs';
import { StripeCardNumberComponent, StripeService } from 'ngx-stripe';
import { StripeCardElementOptions, StripeElementsOptions } from '@stripe/stripe-js';
import { SharedMixpanelService } from '../services/shared-mixpanel.service';

import moment from 'moment';
interface Data {
  senderEmail: string;
  userName: any;
  nameOnCard: any;
  giftedItem: string;
  cardToken: string;
  giftFormData: any;
  timezone: string;
  localDate?: number;
  timeZoneOffset
  giftType
}

@Component({
  selector: 'app-gift-checkout',
  templateUrl: './gift-checkout.component.html',
  styleUrls: ['./gift-checkout.component.scss']
})

export class GiftCheckoutComponent implements OnInit, OnDestroy {
  @ViewChild(StripeCardNumberComponent) card: any = StripeCardNumberComponent;
  @ViewChild('deliveryDateInput') deliveryDateInput: ElementRef;
  public cardOptions: StripeCardElementOptions;
  public elementsOptions: StripeElementsOptions;
  signinForm: UntypedFormGroup;
  resendForm: UntypedFormGroup;
  confirmResendForm: UntypedFormGroup;
  giftForm: UntypedFormGroup;
  isProcessing:boolean = false;
  isLoading:boolean = false;
  confirmCode:boolean = false;
  login:boolean = true;
  isLogin:boolean = false;
  profilePicture;
  userName;
  verified:boolean = false;
  currentSection:string = "personalise";
  minDate: string;
  maxDate: string;
  alreadyHasSubscription:boolean = false;
  senderEmail:string = '';
  senderGivenName = '';
  private emailValueChangesSubscription: Subscription;
  currentValue;
  stripeForm:UntypedFormGroup;
  foundError:boolean = false;
  errorMessage = "An unexpected error occurred.";
  today:boolean = false;
  from = "giftCheckOut";
  constructor(
    public fb: UntypedFormBuilder,public constants:ConstantsService,
    private router: Router,
    private renderer: Renderer2,
    private authenticate: CacheService,
    public userService: UserService,
    private dataService:DataService,
    private urlConstants:UrlConstants,
    private stripeService: StripeService,
    private mixpanel: SharedMixpanelService,
    ) {
  }

  async ngOnInit() {
    this.renderer.setStyle(document.body, 'background-color', '#F2EFEB');
    this.minDate = moment(Date()).format('YYYY-MM-DD');
    this.maxDate = moment(Date()).add(1, 'month').format('YYYY-MM-DD');
    this.intialiseGiftForm();
    
    await this.authcall();
    this.stripeForm = this.fb.group({ name: ['', [Validators.required]] });
  }

  ngAfterViewInit() {
    if (this.card) {
      this.initializeStripeElements();
    }
  }
  
  initializeStripeElements() {
    this.cardOptions = this.constants.SRIPE_CARD_STYLE_GIFT;
    this.elementsOptions = { locale: 'en' };
  }
  public async authcall() {
    await this.authenticate
      .getAuthUser()
      .then(async (data) => {
        let { login, picture, userName, email, given_name } = await data;
        this.currentSection = login ?"personalise":"details";
        this.userName = userName;
        this.senderEmail = email;
        this.senderGivenName = given_name;
        this.isLogin = login;
        this.isLoading = false;
        if (login) {
          await this.userService.getUser(true).then(async data => {
            this.profilePicture = data.pictureUrl === undefined ? picture : data.pictureUrl;
          })
        } else {
          this.intialiseSigninForm();
          this.initialiseResendForm();
        }
      })
  }

  ngOnDestroy(): void {
    // Reset the background color when leaving the component
    this.renderer.removeStyle(document.body, 'background-color');
    // Unsubscribe from all subscriptions to prevent memory leaks
    if (this.emailValueChangesSubscription) {
      this.emailValueChangesSubscription.unsubscribe();
    }
  }

  initialiseResendForm = () => {
    this.resendForm = this.fb.group({
      email: ['', [Validators.email, Validators.required]],
    })
  }

  intialiseSigninForm = () => {
    this.signinForm = this.fb.group({
      email: ['', [Validators.email, Validators.required]],
      password: ['', [Validators.required]]
    })
  }

  initialiseConfirmForm = () => {
    this.confirmResendForm = this.fb.group({
      email: [this.resendForm.value.email],
      code: ['', [Validators.required, Validators.minLength(6),]],
      password: ['', [
        Validators.required,
        Validators.pattern(/^(?=\D*\d)(?=[^a-z]*[a-z])(?=.*[$@#!%*?&])(?=[^A-Z]*[A-Z]).{8,}$/),
      ]]
    })
  }

  intialiseGiftForm = () =>{
    this.giftForm = this.fb.group({
      receipientEmail : ['',[Validators.required,this.customEmailValidator()]],
      receipientName : ['',[Validators.required, Validators.maxLength(100)]],
      senderName : ['',[Validators.required, Validators.maxLength(100)]],
      deliveryDate: [null, [Validators.required, this.dateValidator(this.minDate, this.maxDate)]],
      message:['',[Validators.required, Validators.maxLength(500)]]
    })
    this.emailValueChangesSubscription = this.giftForm.get('receipientEmail')?.valueChanges.subscribe((newValue) => {
      if (newValue !== this.currentValue) {
        this.alreadyHasSubscription = false;
      }
    });
  }

  dateValidator(minDate: string, maxDate: string) {
    return (control: any): { [key: string]: any } | null => {
      const dateValue = control.value;
      if (dateValue) {
        const selectedDate = new Date(dateValue);
        if (selectedDate < new Date(minDate) || selectedDate > new Date(maxDate)) {
          return { invalidDate: { value: control.value } };
        }
      }
      return null;
    };
  }

  customEmailValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const emailRegex = /^[\w.+-]+@([\w-]+\.)+[\w-]{2,4}$/;
      const value = control.value;
      if (!value || emailRegex.test(value)) {
        return null; // Valid email
      }
      return { invalidEmail: true }; // Invalid email
    };
  }

  async onClicklogin() {
    this.isProcessing = true;
    let username = this.signinForm.value.email;
    let password = this.signinForm.value.password;
    try {
      const user = await Auth.signIn(username, password);
      this.isProcessing = false;
      await this.authcall();
    } catch (error) {
      if (error.code === this.constants.COGNITO_ERRORS.USER_NOTCONFIRMED) {
        document.getElementById("resend").style.display = `block`
      }
      else {
        if (error.code === this.constants.COGNITO_ERRORS.NOT_AUTHORIED) {
          document.getElementById("errorAlert").innerHTML = "Your email or password don't match. Try again"
        }
        else {
          document.getElementById("errorAlert").innerHTML = error.message
        }
      }
      this.isProcessing = false;
    }
  }

  onClickSowPassowrd() {
    let inputFiled = (<HTMLInputElement>document.getElementById('input-password'))
    if (inputFiled.type === 'password') {
      inputFiled.type = 'text'
    }
    else {
      inputFiled.type = 'password'
    }
  }

  async onClickResend() {
    this.isLoading = true
    let username = this.signinForm.value.email
    try {
      await Auth.resendSignUp(username);
      document.getElementById("resend").innerHTML = 'Success, Please click the confirmation link send to your email'
      this.isLoading = false;
    } catch (err) {
      document.getElementById("resend").innerHTML = err.message
      this.isLoading = false;
    }
  }

  async onClickConfirmSubmit() {
    this.isProcessing = true
    let username = this.confirmResendForm.value.email;
    let code = this.confirmResendForm.value.code;
    let new_password = this.confirmResendForm.value.password;
    Auth.forgotPasswordSubmit(username, code, new_password)
      .then(data => {
        this.confirmCode = false;
        this.login = true;
        this.isProcessing = false
      })
      .catch(err => {
        document.getElementById('errorAlert').innerHTML = err.message
        this.isProcessing = false
      })
  }

  async onClickVerify() {
    this.initialiseConfirmForm()
    this.isProcessing = true
    let username = this.resendForm.value.email;
    Auth.forgotPassword(username)
      .then(data => {
        this.confirmCode = true
        this.isProcessing = false
      })
      .catch(err => {
        this.isProcessing = false
        document.getElementById('errorAlert').innerHTML = err.message
      });
  }

  onClickForgot() {
    this.login = false;
  }

  onLoginClick(provider) {
    localStorage.setItem('redirectAfterLogin', "/giftCheckout");
    Auth.federatedSignIn({
      provider: provider
    });
  }

  gotoPrevious() {
    switch (this.currentSection) {
      case 'personalise':
        if(this.verified) {
          this.verified = false;
          this.giftForm.get('receipientEmail')?.enable();
        } else {
          this.router.navigate(['/sendGift']);
        }
        break;
      case 'details':
        if(!this.login && !this.confirmCode){
            this.login = true;
          } else if(!this.login && this.confirmCode){
            this.confirmCode = false;
          } else if(this.login && !this.confirmCode){
            this.router.navigate(['/sendGift']);
          }
        break;
      case 'billing':
        this.currentSection = 'personalise';
        this.verified = true;
        this.foundError = false;
        this.giftForm.get('receipientEmail')?.disable();
        break;
    }
  }
  

  async checkReceiverEmail() {
    this.giftForm.get('receipientEmail')?.disable();
    this.currentValue = this.giftForm.get('receipientEmail')?.value;
    const postParams = [
      { param: '{actionName}', value: 'checkUserForGift' }
    ];
    this.isProcessing = true;
    const postdata = {
      receiverEmail: this.giftForm.get('receipientEmail')?.value
    };
    this.dataService.postData(this.urlConstants.getUri(this.urlConstants.actionUnAuth, postParams), postdata)
      .subscribe(async (res:any) => {
        this.isProcessing = false;
        if(res.eligibleForGift){
          this.verified = true;
          this.alreadyHasSubscription = false;
          this.giftForm.get('receipientEmail')?.disable();
        }
        else {
          this.alreadyHasSubscription = true;
          this.verified = false;
          this.giftForm.get('receipientEmail')?.enable();
        }
      },
      (error) => {
        this.isProcessing = false;
        console.error('Error while checking email:', error);
        this.giftForm.get('receipientEmail')?.enable();
      })
  }

  goToNext() {
    this.currentSection = "billing";
      this.initializeStripeElements();
  }


  takeToSignup() {
    localStorage.setItem('redirectAfterLogin', "/giftCheckout");
    this.router.navigate(['/signup']);
  }

  createToken() {
    console.log("create token");
    let checkCardError = (this.card.stripeElementRef.nativeElement).getAttribute("class")
    if (checkCardError.includes(this.constants.EMPTY_TXT)) {
      this.foundError = true;
      this.errorMessage = this.constants.ENTER_CARD_TXT
    }
    else {
      let name = this.stripeForm.get('name').value;
      name === '' ? name = this.senderGivenName : name = name;
      this.isLoading = true;
      this.foundError = false;
      // Genarate stripe token for payment, 
      // this will be used to charge the customer
      this.stripeService.createToken(this.card.element, { name })
        .subscribe((result) => {
          if (result.token) {
            this.handleDeliveryDate();
            // Get the user's timezone in IANA format
            const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
            const userTimeZoneOffset = new Date().getTimezoneOffset();
            let data: Data = {
              senderEmail: this.senderEmail,
              userName: this.userName,
              nameOnCard: name,
              giftedItem: "ONE_YEAR_SUBSCRIPTION_GIFTING",
              cardToken: result.token.id,
              giftFormData: this.giftForm.getRawValue(),
              timezone: userTimezone,
              timeZoneOffset: userTimeZoneOffset,
              giftType:this.constants.giftType.scheduled
          }

          if(this.today){
            data.localDate = Date.now();
            data.giftType = this.constants.giftType.today;
          }
            this.makePayment(data);
          }
          if (result.error) {
            this.isLoading = false;
            this.foundError = true;
            this.errorMessage = result.error.message
          }
        });
    }
  }

handleDeliveryDate() {
  this.today = false;
    const selectedDate = this.giftForm.value.deliveryDate;
    const todayDate = this.minDate;
    if (selectedDate === todayDate) {
      this.today =true;
    }
}

  async makePayment(data){

    this.mixpanel.actions.track("Prime payment", {
      "Email": data.senderEmail,
      "Name": data.userName,
      "Amount": 50,
      "Type": "Gift"
    });

    const params = [{ param: '{actionName}', value: 'makeGiftPayment' }];
    const bodyObj = data;
    await this.dataService
      .postData(
        this.urlConstants.getUri(this.urlConstants.action, params),
        JSON.stringify(bodyObj)
      )
      .subscribe(async (res:any) => {
        this.isLoading = false;
        if(res.status == "success") {
          this.authenticate.setGiftPayment(true);
          this.router.navigate(['/giftSuccess']);
        }
      },
      (error) => {
        this.foundError = true;
        this.isLoading = false;
        this.errorMessage = error?.error?.msg;
      })
  }

  // Method to open the calendar
  openCalendar() {
    if (this.deliveryDateInput) {
      const inputElement = this.deliveryDateInput.nativeElement;
      if (inputElement) {
        inputElement.showPicker(); // Triggers the calendar popup
      }
    }
  }
}
